import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { trackPageView } from "@/lib/analytics";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { initErrorTracking } from "@/services/errorTrackingService";

// Eager load homepage for best FCP
import Index from "@/pages/Index";
import ChatBot from "@/components/ChatBot";
import PushPromptOnLogin from "@/components/PushPromptOnLogin";
import CookieConsent from "@/components/CookieConsent";

// Lazy load all other pages
const Booking = lazy(() => import("@/pages/Booking"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const Equipment = lazy(() => import("@/pages/Equipment"));
const Surroundings = lazy(() => import("@/pages/Surroundings"));
const Activities = lazy(() => import("@/pages/Activities"));
const Access = lazy(() => import("@/pages/Access"));
const Contact = lazy(() => import("@/pages/Contact"));
const Legal = lazy(() => import("@/pages/Legal"));
const Auth = lazy(() => import("@/pages/Auth"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));

const Profile = lazy(() => import("@/pages/Profile"));
const AdminPage = lazy(() => import("@/pages/Admin"));
const Marketing = lazy(() => import("@/pages/Marketing"));
const UserReservations = lazy(() => import("@/pages/UserReservations"));
const ReservationSuccess = lazy(() => import("@/pages/ReservationSuccess"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const AddressForm = lazy(() => import("@/pages/AddressForm"));
const SignContract = lazy(() => import("@/pages/SignContract"));
const ReservationDirectory = lazy(() => import("@/pages/ReservationDirectory"));
const ClientDetails = lazy(() => import("@/pages/ClientDetails"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const ViewQuote = lazy(() => import("@/pages/ViewQuote"));
const QuoteClientInfo = lazy(() => import("@/pages/QuoteClientInfo"));
const QuoteVerification = lazy(() => import("@/pages/QuoteVerification"));
const CheckInForm = lazy(() => import("@/pages/CheckInForm"));
const TemplatePreview = lazy(() => import("@/pages/TemplatePreview"));
const Expenses = lazy(() => import("@/pages/Expenses"));
const LocalizedRoute = lazy(() => import("@/components/LocalizedRoute"));
const LinkReservation = lazy(() => import("@/pages/LinkReservation"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogArticle = lazy(() => import("@/pages/BlogArticle"));
const CasesPage = lazy(() => import("@/pages/Cases"));
const AuthorizeDeposit = lazy(() => import("@/pages/AuthorizeDeposit"));
const Promotions = lazy(() => import("@/pages/Promotions"));
const FriendsAvailability = lazy(() => import("@/pages/FriendsAvailability"));
const GouvernanteDashboard = lazy(() => import("@/pages/GouvernanteDashboard"));
const ReviewForm = lazy(() => import("@/pages/ReviewForm"));

import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { useFrenchRouteSync } from "@/hooks/useFrenchRouteSync";

const queryClient = new QueryClient();

const AppContent = () => {
  // Sync language to French when on French routes
  useFrenchRouteSync();
  const location = useLocation();

  // Canonical auto-référencé sur le domaine principal (www), une seule balise par page
  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, "") || "/";
    const href = `https://www.villadoucevague.fr${path}`;
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = href;
    document.head.querySelector('meta[property="og:url"]')?.setAttribute("content", href);
  }, [location.pathname]);

  // Page vue GA4 à chaque navigation (hors admin), après mise à jour du titre
  useEffect(() => {
    const p = location.pathname;
    if (/^\/(admin|marketing|expenses|gouvernante)/.test(p)) return;
    const t = setTimeout(() => trackPageView(p + location.search), 300);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);
  
  // Hide chatbot on admin pages
  const isAdminRoute = location.pathname.startsWith('/admin') || 
                       location.pathname.startsWith('/marketing') ||
                       location.pathname.startsWith('/expenses') ||
                       location.pathname.startsWith('/gouvernante') ||
                       location.pathname.startsWith('/review/');
  
  return (
    <>
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
        <Routes>
          {/* Non-localized routes */}
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/cases" element={<CasesPage />} />
          <Route path="/marketing" element={<Marketing />} />
          <Route path="/admin/client/:identifier" element={<ClientDetails />} />
          <Route path="/admin/template-preview/:templateId" element={<TemplatePreview />} />
          <Route path="/expenses" element={<Expenses />} />
          <Route path="/link-reservation" element={<LinkReservation />} />
          <Route path="/reservation-success" element={<ReservationSuccess />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/address-form/:token" element={<AddressForm />} />
          <Route path="/sign-contract/:token" element={<SignContract />} />
          <Route path="/reservation-directory/:reservationId" element={<ReservationDirectory />} />
          <Route path="/quote/:quoteId" element={<ViewQuote />} />
          <Route path="/quote/:quoteId/client-info" element={<QuoteClientInfo />} />
          <Route path="/quote/:quoteId/verification" element={<QuoteVerification />} />
          <Route path="/check-in/:token" element={<CheckInForm />} />
          <Route path="/unsubscribe/:token?" element={<Unsubscribe />} />
          <Route path="/authorize-deposit/:token" element={<AuthorizeDeposit />} />
          <Route path="/friends" element={<FriendsAvailability />} />
          <Route path="/gouvernante" element={<GouvernanteDashboard />} />
          <Route path="/review/:bookingId" element={<ReviewForm />} />
          
          {/* French routes (default) */}
          <Route path="/" element={<Index />} />
          <Route path="/galerie" element={<Gallery />} />
          <Route path="/equipements" element={<Equipment />} />
          <Route path="/reserver" element={<Booking />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/environs" element={<Surroundings />} />
          <Route path="/activites" element={<Activities />} />
          <Route path="/acces" element={<Access />} />
          <Route path="/mentions-legales" element={<Legal />} />
          <Route path="/mes-reservations" element={<UserReservations />} />
          <Route path="/profil" element={<Profile />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/promotions" element={<Promotions />} />
          
          {/* Localized routes for other languages */}
          <Route path="/:lang/*" element={<LocalizedRoute />} />
          
          {/* Catch all route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      
      {/* ChatBot - only show on public pages */}
      {!isAdminRoute && <ChatBot />}

      {/* Push prompt for admin/gouvernante after login */}
      <PushPromptOnLogin />
    </>
  );
};

function App() {
  useEffect(() => {
    initErrorTracking();
  }, []);

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LanguageProvider>
            <Toaster />
            <AppContent />
            <CookieConsent />
          </LanguageProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default App;
